import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { motion } from "framer-motion";
import { ArrowUpRight, CheckCircle2, Loader2, Mail, MapPin, Phone, Clock } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { PageHeader } from "../components/site/Sections";
import { Reveal } from "../components/site/Reveal";
import { Seo } from "../components/site/Seo";
import { CONTACT, SERVICE_OPTIONS } from "../data/site";
import { api, errorText } from "../lib/api";

const schema = z.object({
  name: z.string().trim().min(2, "Please enter your name"),
  business: z.string().trim().min(1, "Please enter your business name"),
  email: z.string().trim().email("Please enter a valid email address"),
  phone: z.string().trim().optional().refine((v) => !v || /^[+()\d\s-]{8,20}$/.test(v), "Please enter a valid phone number"),
  service: z.string().min(1, "Please choose a service"),
  message: z.string().trim().min(10, "Tell us a little more (at least 10 characters)"),
});

const inputCls = "w-full rounded-xl border bg-white px-4 py-3.5 text-[15px] text-ink outline-none transition-colors placeholder:text-ink/40 focus:border-ink";

const Field = ({ label, error, children, optional, id }) => (
  <div>
    <label htmlFor={id} className="mb-2 block text-sm font-semibold text-ink">{label} {optional && <span className="font-normal text-ink/55">(optional)</span>}</label>
    {children}
    {error && <p className="mt-1.5 text-sm text-flame-dark" role="alert" data-testid={`contact-error-${id}`}>{error}</p>}
  </div>
);

const ContactForm = () => {
  const [params] = useSearchParams();
  const preset = SERVICE_OPTIONS.includes(params.get("service")) ? params.get("service") : "";
  const [sent, setSent] = useState(false);
  const [serverError, setServerError] = useState("");
  const { register, handleSubmit, control, reset, formState: { errors, isSubmitting } } = useForm({
    resolver: zodResolver(schema),
    defaultValues: { name: "", business: "", email: "", phone: "", service: preset, message: "" },
  });

  const onSubmit = async (values) => {
    setServerError("");
    try {
      await api.post("/enquiries", { ...values, phone: values.phone || null });
      setSent(true);
      reset();
    } catch (e) {
      setServerError(errorText(e));
    }
  };

  const b = (k) => (errors[k] ? "border-flame" : "border-ink/15");

  return (
    <>
      {sent ? (
        <motion.div key="ok" initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} className="flex min-h-[520px] flex-col items-start justify-center rounded-[1.75rem] bg-ink p-10 text-canvas" data-testid="contact-success">
          <CheckCircle2 className="h-14 w-14 text-flame" />
          <h2 className="mt-8 font-display text-4xl font-bold tracking-tight">Message received.</h2>
          <p className="mt-4 max-w-md text-neutral-300">We'll be in touch within one business day to book your discovery call. A confirmation is on its way to your inbox, too.</p>
          <button onClick={() => setSent(false)} className="mt-10 rounded-full border border-canvas/25 px-6 py-3 text-sm font-semibold transition-colors hover:bg-canvas hover:text-ink" data-testid="contact-send-another">Send another message</button>
        </motion.div>
      ) : (
        <motion.form key="form" initial={{ opacity: 0 }} animate={{ opacity: 1 }} onSubmit={handleSubmit(onSubmit)} noValidate
          className="grid gap-5 rounded-[1.75rem] border border-ink/10 bg-white p-6 sm:grid-cols-2 sm:p-10" data-testid="contact-form">
          <Field label="Your name" id="name" error={errors.name?.message}>
            <input id="name" autoComplete="name" className={`${inputCls} ${b("name")}`} {...register("name")} data-testid="contact-name-input" />
          </Field>
          <Field label="Business name" id="business" error={errors.business?.message}>
            <input id="business" autoComplete="organization" className={`${inputCls} ${b("business")}`} {...register("business")} data-testid="contact-business-input" />
          </Field>
          <Field label="Email" id="email" error={errors.email?.message}>
            <input id="email" type="email" autoComplete="email" className={`${inputCls} ${b("email")}`} {...register("email")} data-testid="contact-email-input" />
          </Field>
          <Field label="Phone" id="phone" optional error={errors.phone?.message}>
            <input id="phone" type="tel" autoComplete="tel" className={`${inputCls} ${b("phone")}`} {...register("phone")} data-testid="contact-phone-input" />
          </Field>
          <div className="sm:col-span-2">
            <Field label="What can we help with?" id="service" error={errors.service?.message}>
              <Controller name="service" control={control} render={({ field }) => (
                <Select value={field.value} onValueChange={field.onChange}>
                  <SelectTrigger id="service" className={`h-auto rounded-xl bg-white px-4 py-3.5 text-[15px] ${b("service")}`} data-testid="contact-service-select">
                    <SelectValue placeholder="Choose a service" />
                  </SelectTrigger>
                  <SelectContent className="bg-white" data-lenis-prevent>
                    {SERVICE_OPTIONS.map((o) => <SelectItem key={o} value={o} data-testid={`contact-service-option-${o.toLowerCase().replace(/\s+/g, "-")}`}>{o}</SelectItem>)}
                  </SelectContent>
                </Select>
              )} />
            </Field>
          </div>
          <div className="sm:col-span-2">
            <Field label="Message" id="message" error={errors.message?.message}>
              <textarea id="message" rows={5} placeholder="What's working, what isn't, and where you'd like to be" className={`${inputCls} resize-none ${b("message")}`} {...register("message")} data-testid="contact-message-input" />
            </Field>
          </div>
          {serverError && <p className="text-sm text-flame-dark sm:col-span-2" role="alert" data-testid="contact-server-error">{serverError}</p>}
          <div className="flex flex-col gap-4 sm:col-span-2 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-xs text-ink/60">We'll only use your details to reply. No surprise newsletters.</p>
            <button type="submit" disabled={isSubmitting} className="btn-primary !px-8 !py-4 disabled:opacity-70" data-testid="contact-submit-button">
              {isSubmitting ? <><Loader2 className="h-4 w-4 animate-spin" /> Sending…</> : <>Send enquiry <ArrowUpRight className="h-4 w-4" /></>}
            </button>
          </div>
        </motion.form>
      )}
    </>
  );
};

const InfoRow = ({ icon: Icon, label, children }) => (
  <div className="flex gap-4 border-t border-ink/10 py-5">
    <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-ink text-canvas"><Icon className="h-4 w-4" /></span>
    <div><p className="text-xs font-semibold uppercase tracking-[0.16em] text-ink/55">{label}</p><div className="mt-1 text-ink">{children}</div></div>
  </div>
);

export default function Contact() {
  return (
    <>
      <Seo path="/contact" title="Contact us" description="Book a free discovery call with The Catalyst, a Canberra marketing agency. Tell us about your goals and we'll be in touch within one business day." />
      <PageHeader eyebrow="Contact" lines={["Let's get you", "moving."]} intro="Tell us what you're working on. We'll reply within one business day to book a free 30-minute discovery call. No hard sell. Promise." />
      <section className="wrap grid gap-10 pb-20 lg:grid-cols-12">
        <Reveal className="lg:col-span-7"><ContactForm /></Reveal>
        <Reveal delay={0.1} className="lg:col-span-5">
          <InfoRow icon={MapPin} label="Office"><address className="not-italic" data-testid="contact-address">{CONTACT.address}<br />{CONTACT.suburb}</address></InfoRow>
          <InfoRow icon={Phone} label="Phone"><a href={CONTACT.phoneHref} className="hover:text-flame" data-testid="contact-phone-link">{CONTACT.phoneDisplay}</a></InfoRow>
          <InfoRow icon={Mail} label="Email"><a href={`mailto:${CONTACT.email}`} className="break-all hover:text-flame" data-testid="contact-email-link">{CONTACT.email}</a></InfoRow>
          <InfoRow icon={Clock} label="Hours">{CONTACT.hours}</InfoRow>
          <div className="mt-4 grid grid-cols-2 gap-3 md:hidden">
            <a href={CONTACT.phoneHref} className="btn-ghost" data-testid="contact-click-to-call"><Phone className="h-4 w-4" /> Call</a>
            <a href={`mailto:${CONTACT.email}`} className="btn-primary" data-testid="contact-click-to-email"><Mail className="h-4 w-4" /> Email</a>
          </div>
        </Reveal>
      </section>
      <section className="wrap pb-20 sm:pb-28">
        <div className="aspect-[4/3] overflow-hidden rounded-[1.75rem] border border-ink/10 bg-muted sm:aspect-[21/8]">
          <iframe title="The Catalyst office location" src={CONTACT.mapSrc} loading="lazy" referrerPolicy="no-referrer-when-downgrade"
            className="h-full w-full grayscale-[0.6] contrast-[1.05]" data-testid="contact-map" />
        </div>
      </section>
    </>
  );
}
